import { getRequest, getRequestWithParamsAndQuery, getRequestWithQuery, getRequestWithAuth, postRequest, postRequestWithAuth } from "../configuration";
import getApiWithVersion from "../apis";
import { API_VERSION } from "../../constants/common";
import API_PARAM from '../constant';
import type { Tparams } from '../configuration';
import type { IhomeContent, IhomeBanner, IfaqApiResponse, IapiResponse_appliances, IapiResponse_contact_us, IapiResponse_media_logo, IapiResponse_blog_list, IapiResponse_blog_post_category, IapiResponse_blog_post_detail, IapiResponse_session, IApiResponse_productSearch, IapiResponse_brand, IseoMetaDetails, IapiResponse_product_categories } from '../../../types';
import type { Auth } from "./auth";
import auth from "./auth";
import product, { Product } from "./product";
import seo, { SEO } from "./seo";
import cart, { Cart } from "./cart";
import utm, { UtmQuery } from "./utm";
import coupon, { Coupon } from "./coupon";
import address, { API_Address } from "./address";
import warehouse, { API_Warehouse } from "./warehouse";
import voucher, { API_Voucher } from "./voucher";
import order, { API_Order } from "./order";
import activity, { Activity } from "./activity";
import referral, { API_Referral } from "./referral";
import user, { API_User } from "./user";
import { update, Update } from "./update";
import static_page, { StaticPage } from "./staticPage";


const api = getApiWithVersion(API_VERSION.V1);
export interface API_V1 {
  sendError(key: string, apiName: string): void;
  getAboutUs(): Promise<any>;
  getSeoMetaDetails(pageCode: string): Promise<IseoMetaDetails>;
  getHomeContent(pageCode: string): Promise<IhomeContent>;
  getHomePageBanner(): Promise<IhomeBanner>;
  getHomePageBannerWithQuery(query: any): Promise<IhomeBanner>;
  getHeavyAppliances(): Promise<IapiResponse_appliances>;
  getLightAppliances(): Promise<IapiResponse_appliances>;
  getElectricAppliances(): Promise<IapiResponse_appliances>;
  getAllFaqs(): Promise<IfaqApiResponse>;
  getContactUs(): Promise<IapiResponse_contact_us>;
  getMediaLogo(): Promise<IapiResponse_media_logo>;
  getBlogList(query: { params?: Tparams, query?: any }): Promise<IapiResponse_blog_list>;
  getBlogCategory(): Promise<IapiResponse_blog_post_category>;
  getBlogPostDetail(post_id: string | undefined): Promise<IapiResponse_blog_post_detail>;
  getSessionCart(sessionId: string | undefined): Promise<IapiResponse_session>
  getProductSearch(productSlug: string): Promise<IApiResponse_productSearch>
  getBrand(categoryId: string): Promise<IapiResponse_brand>;
  getUserLog(param: Tparams, query: any): Promise<any>
  getProductCategories(): Promise<IapiResponse_product_categories>;
  checkProductServiceDetails(query: any): Promise<any>;
  getChatWidget({ message, payload, chathistory, selsmartToken, xSessionToken }: { message: string, payload: { label: string, categoryId: string, pincode: string } | null, chathistory?: { role: string, text: string, ts: string }[], selsmartToken?: string, xSessionToken?: string }): Promise<any>;


  update: Update
  auth: Auth;
  product: Product
  seo: SEO
  cart: Cart
  utm: UtmQuery
  coupon: Coupon;
  address: API_Address;
  warehouse: API_Warehouse;
  voucher: API_Voucher,
  order: API_Order,
  activity: Activity,
  referral: API_Referral;
  user: API_User;
  staticPage: StaticPage;


}

const api_v1: API_V1 = {
  sendError(key: string, apiName: string) {
    if (!key)
      throw new Error(`'${key}' is missing in '${apiName}' api`)
  },
  getAboutUs() {
    return getRequest(api.FEEDBACK_ABOUT_US);
  },
  getSeoMetaDetails(pageCode) {
    return getRequest(api.SEO_META_DETAILS, pageCode)
  },
  getHomeContent(pageCode) {
    return getRequest(api.HOME_CONTENT, pageCode)
  },
  getHomePageBanner() {
    return getRequest(api.APPLIANCES, API_PARAM.BANNER.HOME_PAGE)
  },
  getHomePageBannerWithQuery(query) {
    return getRequestWithParamsAndQuery(api.APPLIANCES, API_PARAM.BANNER.HOME_PAGE, { query: query })
  },
  getElectricAppliances() {
    return getRequest(api.APPLIANCES, API_PARAM.APPLIANCES.ELECTRIC_APPLIANCES)
  },
  getHeavyAppliances() {
    return getRequest(api.APPLIANCES, API_PARAM.APPLIANCES.HEAVY_APPLIANCES)
  },
  getLightAppliances() {
    return getRequest(api.APPLIANCES, API_PARAM.APPLIANCES.LIGHT_APPLIANCES)
  },
  getAllFaqs() {
    return getRequest(api.ALL_FAQS)
  },
  getContactUs() {
    return getRequest(api.CONTACT_US, API_PARAM.CONTACT_US.DETAILS)
  },
  getMediaLogo() {
    return getRequest(api.MEDIA_LOGO)
  },
  getBlogList(query) {
    return getRequestWithQuery(api.BLOG_LIST, query);
  },
  getBlogCategory() {
    return getRequest(api.BLOG_CATEGORY);
  },
  getBlogPostDetail(post_id) {
    return getRequest(api.BLOG_POST_DETAIL, post_id);
  },
  getSessionCart(sessionId) {
    if (!sessionId) throw new Error('SessionId is missing in getSessionCart api')
    return getRequest(api.SESSION_CART, sessionId)
  },
  getProductSearch(productSlug) {
    if (!productSlug) throw new Error('productSlug is missing in getProductSearch api')
    return getRequest(api.PRODUCT_SEARCH, productSlug)
  },
  getBrand(categoryId) {
    this.sendError(categoryId, 'getBrand');
    return getRequest(api.BRAND, categoryId)
  },
  getUserLog(parms, query) {
    return getRequestWithParamsAndQuery(api.USER_LOG, parms, query)
  },
  getProductCategories() {
    return getRequest(api.PRODUCT_CATEGORIES)
  },

  getChatWidget({ message, payload, chathistory, selsmartToken, xSessionToken }) {
    const requestPayload = { message, payload, chathistory, selsmartToken, xSessionToken }
    return postRequestWithAuth(api.CHAT_WIDGET, requestPayload);
  },
  update: update,
  auth: auth,
  product: product,
  seo: seo,
  cart: cart,
  utm: utm,
  coupon: coupon,
  address: address,
  warehouse: warehouse,
  voucher: voucher,
  order: order,
  activity: activity,
  referral: referral,
  user: user,
  staticPage: static_page,

  checkProductServiceDetails(body) {
    return postRequest(api.PRODUCT_SERVICE, body)
  },
};

export default api_v1;
